import { Settings } from 'lucide-react';

import Github from '@/assets/ico/vendor/github.svg?c';
import Gitlab from '@/assets/ico/vendor/gitlab.svg?c';
import Bitbucket from '@/assets/ico/vendor/bitbucket.svg?c';
import Gitea from '@/assets/ico/vendor/gitea.svg?c';
import Azure from '@/assets/ico/vendor/azure.svg?c';

import { IconType } from '@@/Icon';

import { AuthTypeOption, Provider } from '../types';

export type ProviderConfig = {
  icon: IconType;
  title: string;
  description: string;
  fixedAuthType?: AuthTypeOption;
  usernameTooltip?: string;
  passwordLabel: string;
  passwordTooltip?: string;
};

export const providerConfigs: Record<Provider, ProviderConfig> = {
  [Provider.GitHub]: {
    icon: Github,
    title: 'GitHub',
    description:
      'Authenticate with a Personal Access Token. The token is used as the password in Basic Auth, this is how GitHub expects it.',
    fixedAuthType: AuthTypeOption.Basic,
    usernameTooltip: 'Your GitHub username',
    passwordLabel: 'Personal Access Token',
    passwordTooltip: 'A fine-grained or classic PAT with repo access.',
  },
  [Provider.GitLab]: {
    icon: Gitlab,
    title: 'GitLab',
    description:
      'Authenticate with a Personal Access Token. GitLab uses the token as the password in Basic Auth.',
    fixedAuthType: AuthTypeOption.Basic,
    usernameTooltip: 'Your GitLab username',
    passwordLabel: 'Personal Access Token',
    passwordTooltip: 'A PAT with read_repository or api scope.',
  },
  [Provider.BitbucketCloud]: {
    icon: Bitbucket,
    title: 'Bitbucket Cloud',
    description:
      'Authenticate with an App Password. Bitbucket Cloud uses App Passwords for Basic Auth.',
    fixedAuthType: AuthTypeOption.Basic,
    usernameTooltip: 'Your Bitbucket username',
    passwordLabel: 'App Password',
    passwordTooltip: 'A Bitbucket App Password with repository read access.',
  },
  [Provider.BitbucketDataCenter]: {
    icon: Bitbucket,
    title: 'Bitbucket Data Center',
    description:
      'Authenticate with a Personal Access Token. Bitbucket Data Center uses the token in Bearer Token Auth.',
    fixedAuthType: AuthTypeOption.Token,
    usernameTooltip: 'Your Bitbucket username',
    passwordLabel: 'Personal Access Token',
    passwordTooltip: 'A PAT with repository read access.',
  },
  [Provider.AzureDevOps]: {
    icon: Azure,
    title: 'Azure DevOps',
    description:
      'Authenticate with a Personal Access Token. Azure DevOps uses Basic Authentication.',
    fixedAuthType: AuthTypeOption.Basic,
    usernameTooltip: 'Your Azure DevOps username',
    passwordLabel: 'Personal Access Token',
    passwordTooltip: 'A PAT with Code (Read) permission.',
  },
  [Provider.GiteaForgejo]: {
    icon: Gitea,
    title: 'Gitea / Forgejo',
    description: 'Authenticate with an Access Token for Gitea or Forgejo.',
    fixedAuthType: AuthTypeOption.Token,
    usernameTooltip: 'Your Gitea/Forgejo username',
    passwordLabel: 'Access Token',
    passwordTooltip: 'An API access token with repository read access.',
  },
  [Provider.Custom]: {
    icon: Settings,
    title: 'Custom',
    description:
      'Configure authentication manually for any Git provider. Choose Basic or Token auth.',
    usernameTooltip: 'Your Git username',
    passwordLabel: 'Password',
  },
};
